import { useState, useMemo } from "react"
import BuyerCampaignDetail from "./BuyerCampaignDetail"
import { useDeleteBuyerCampaignMutation } from "apis/buyerCampaign"
import Button from "components/ui/Button"
import { FiTrash2 } from "react-icons/fi"
import Modal from "components/ui/Modal"
import moment from "moment"
import Table from "components/ui/Table"

const BuyerCampaigns = ({ campaigns, templates }) => {
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const pageSize = 10

  const {
    mutate: deleteCampaign,
    isPending: isDeleting,
  } = useDeleteBuyerCampaignMutation()

  const handleDeleteClick = (campaign) => {
    setSelectedCampaign(campaign)
    setIsDeleteModalOpen(true)
  }

  const handleDeleteConfirm = () => {
    deleteCampaign(selectedCampaign._id)
    setIsDeleteModalOpen(false)
    setSelectedCampaign(null)
  }

  const handleRowClick = (campaign) => {
    setSelectedCampaign(campaign)
  }

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800"
      case "active":
        return "bg-green-100 text-green-800"
      case "completed":
        return "bg-lightgray text-darkergray"
      default:
        return "bg-lightgray text-darkergray"
    }
  }

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "propertyId.address",
        header: "Property",
        cell: (row) => row.propertyId?.address || "Unknown Property",
        isFilter: true,
      },
      {
        accessorKey: "userId.name",
        header: "User",
        cell: (row) => row.userId?.name || "Unknown",
        isFilter: true,
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: (row) => (
          <span className="capitalize">
            {row.type === "privateTreaty" ? "Private Treaty" : row.type || "-"}
          </span>
        ),
        isFilter: true,
      },
      {
        accessorKey: "closingDate",
        header: "Closing Date",
        cell: (row) =>
          row.closingDate
            ? moment(row.closingDate).format("YYYY-MM-DD")
            : "-",
        isFilter: false,
      },
      {
        accessorKey: "messages",
        header: "Messages",
        cell: (row) => row.messages?.length || 0,
        isFilter: false,
      },
      {
        accessorKey: "recipients",
        header: "Recipients",
        cell: (row) => row.recipients?.length || 0,
        isFilter: false,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (row) => (
          <span
            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(row.status)}`}
          >
            {row.status?.charAt(0).toUpperCase() + row.status?.slice(1)}
          </span>
        ),
        isFilter: true,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleDeleteClick(row)
              }}
              className="delete-icon"
              title="Delete Campaign"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "80px",
      },
    ],
    []
  )

  // If a campaign is selected, show detail view
  if (selectedCampaign && !isDeleteModalOpen) {
    return (
      <BuyerCampaignDetail
        campaign={selectedCampaign}
        templates={templates || []}
        onBack={() => setSelectedCampaign(null)}
      />
    )
  }

  return (
    <div>
      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false)
          setSelectedCampaign(null)
        }}
        title="Delete Campaign"
      >
        <p className="text-sm text-darkgray mb-4">
          Are you sure you want to delete this buyer campaign for "
          {selectedCampaign?.propertyId?.address || "Unknown Property"}"? This action cannot be
          undone.
        </p>
        <div className="flex justify-end space-x-3">
          <Button
            onClick={() => {
              setIsDeleteModalOpen(false)
              setSelectedCampaign(null)
            }}
            className="outline-button"
          >
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} className="black-button">
            Delete
          </Button>
        </div>
      </Modal>

      {/* Table */}
      <div className="container mx-auto">
        <Table
          title=""
          columns={columns}
          data={campaigns || []}
          pageSize={pageSize}
          onRowClick={handleRowClick}
        />
      </div>
    </div>
  )
}

export default BuyerCampaigns
